export interface WizardStep {
  title: string;
  description: string;
  /** key into the wizard step component map */
  componentKey: string;
}

export interface PackageDef {
  id: string;
  name: string;
  tagline: string;
  description: string;
  icon: string;
  accentColor: string;
  features: string[];
  wizardSteps: WizardStep[];
  /** which subscription flags this package requires (informational) */
  requiredFlags: string[];
  /** internal only — true = only show to Velaro team */
  internalOnly?: boolean;
}

// ── Tier Comparison Data ──────────────────────────────────────────────────────
// Internal reference for pricing/packaging decisions.
// Every limit is a real finite number — never "Unlimited". Enterprise gets high-but-finite caps.
// Competitor data: current as of Q1 2026 — update periodically.

export interface TierLimit {
  label: string;
  /** Brief description of what this limit does and whether it's enforced */
  description: string;
  /** Is this currently enforced in code (blocks the action) vs just stored */
  enforced: boolean;
  tiers: {
    free: string;
    starter: string;
    professional: string;
    enterprise: string;
  };
  competitors: {
    name: string;
    value: string;
  }[];
}

export const TIER_LIMITS: TierLimit[] = [
  // ── Content & Knowledge ──────────────────────────────────────────────────
  {
    label: "KB Articles",
    description: "Max published knowledge base articles. More = higher Azure Search indexing cost.",
    enforced: false,
    tiers: { free: "25", starter: "500", professional: "5,000", enterprise: "50,000" },
    competitors: [
      { name: "Zendesk (free)", value: "50" },
      { name: "Help Scout ($25/mo)", value: "Unlimited" },
      { name: "Intercom (Growth)", value: "Unlimited" },
      { name: "Freshdesk (free)", value: "Unlimited" }
    ]
  },
  {
    label: "KB Reindex Interval",
    description: "How often KbReindexService triggers a full reindex into Azure AI Search. Index uses BM25 lexical matching (EnMicrosoft analyzer) + Azure semantic ranker (Microsoft cross-encoder re-scores top BM25 results — no custom embedding API calls, requires Standard tier). Unified index covers KB articles AND scraper results. Lower interval = fresher bot answers but higher Azure Search API cost.",
    enforced: true,
    tiers: { free: "24h (daily)", starter: "24h (daily)", professional: "4h", enterprise: "1h" },
    competitors: [
      { name: "Algolia (free tier)", value: "Real-time on write" },
      { name: "Azure AI Search", value: "Real-time on write (we batch for cost)" },
      { name: "Elasticsearch", value: "Real-time on write" },
      { name: "Typesense (self-host)", value: "Real-time on write" }
    ]
  },
  // ── Contacts / CRM ────────────────────────────────────────────────────────
  {
    label: "Max Contacts",
    description: "Total contacts stored (campaign email lists + CRM). Enforced by CampaignQuotaService — blocks CSV import and form capture when exceeded.",
    enforced: true,
    tiers: { free: "500", starter: "10,000", professional: "100,000", enterprise: "1,000,000" },
    competitors: [
      { name: "HubSpot (free)", value: "1,000,000 (no limit)" },
      { name: "Mailchimp (free)", value: "500" },
      { name: "ActiveCampaign (Lite)", value: "No limit (charges per contact)" },
      { name: "Zoho CRM (free)", value: "10,000" },
      { name: "Brevo (free)", value: "Unlimited contacts, 300 emails/day" }
    ]
  },
  // ── Email Campaigns ───────────────────────────────────────────────────────
  {
    label: "Outbound Emails / Month",
    description: "Hard cap enforced by CampaignQuotaService — blocks sends when exceeded, resets on 1st of month. Azure ACS cost is ~$0.00028/email (~$0.28 per 1,000). Higher tiers = higher costs to us, price accordingly.",
    enforced: true,
    tiers: { free: "1,000", starter: "5,000", professional: "50,000", enterprise: "500,000" },
    competitors: [
      { name: "Mailchimp (free)", value: "1,000/mo" },
      { name: "Brevo (free)", value: "9,000/mo (300/day cap)" },
      { name: "HubSpot (free)", value: "2,000/mo" },
      { name: "Mailchimp (Essentials)", value: "10× contacts/mo" },
      { name: "ActiveCampaign (Lite)", value: "10× contacts/mo" },
      { name: "Klaviyo (free)", value: "500/mo" }
    ]
  },
  // ── Chat / Live Conversations ─────────────────────────────────────────────
  {
    label: "Deployments (Chat Widgets)",
    description: "Number of unique chat widget embed codes (one per website/brand/subdomain). Enforced in DeploymentController.",
    enforced: true,
    tiers: { free: "1", starter: "3", professional: "25", enterprise: "250" },
    competitors: [
      { name: "Intercom (Starter)", value: "1 workspace" },
      { name: "Zendesk (Suite Team)", value: "Unlimited brands" },
      { name: "Freshchat (Growth)", value: "Unlimited widgets" },
      { name: "Drift (free)", value: "1 deployment" }
    ]
  },
  {
    label: "Max Turns / Conversation",
    description: "Max visitor messages per chat. 0 = unlimited. Enforced in ConversationService — graceful close on limit. Prevents bot abuse loops.",
    enforced: true,
    tiers: { free: "50", starter: "50", professional: "100", enterprise: "200" },
    competitors: [
      { name: "Intercom", value: "~500 (undocumented)" },
      { name: "Zendesk", value: "Unlimited" },
      { name: "Freshchat", value: "Unlimited" },
      { name: "Drift", value: "Unlimited" }
    ]
  },
  // ── Automation / Workflows ────────────────────────────────────────────────
  {
    label: "Active Workflows",
    description: "Published workflow definitions. Not yet enforced — store target for sprint. Workflow runs are separately metered (see MaxWorkflowRunsPerMonth).",
    enforced: false,
    tiers: { free: "3", starter: "25", professional: "250", enterprise: "2,500" },
    competitors: [
      { name: "Zapier (free)", value: "5 Zaps" },
      { name: "Make (free)", value: "2 scenarios" },
      { name: "HubSpot (free)", value: "5 workflows" },
      { name: "ActiveCampaign (Lite)", value: "Unlimited" },
      { name: "n8n (self-host)", value: "Unlimited" }
    ]
  },
  {
    label: "Workflow Runs / Month",
    description: "Every workflow execution regardless of trigger. Enforced by WorkflowJobRunner. This is the primary monetization lever — price tiers here directly against Zapier/Make.",
    enforced: true,
    tiers: { free: "100", starter: "10,000", professional: "100,000", enterprise: "1,000,000" },
    competitors: [
      { name: "Zapier (free)", value: "100/mo" },
      { name: "Make (free)", value: "1,000 ops/mo" },
      { name: "HubSpot (free)", value: "1,000/mo" },
      { name: "Zapier (Starter $19.99)", value: "750/mo" },
      { name: "Make (Core $9)", value: "10,000 ops/mo" },
      { name: "n8n (free cloud)", value: "2,500 executions/mo" }
    ]
  },
  // ── AI / MCP ──────────────────────────────────────────────────────────────
  {
    label: "MCP Tool Servers",
    description: "External MCP tool servers per site. Each server = one API key + tool discovery. Enforced at server addition. Higher = more AI capability but higher latency/cost risk.",
    enforced: true,
    tiers: { free: "0", starter: "3", professional: "10", enterprise: "50" },
    competitors: [
      { name: "Intercom (AI)", value: "Proprietary tools only" },
      { name: "Zendesk (AI)", value: "Proprietary tools only" },
      { name: "Cursor / Claude", value: "Unlimited MCP servers" },
      { name: "n8n (AI agent)", value: "Unlimited tools" }
    ]
  },
  {
    label: "MCP Calls / Month",
    description: "Monthly AI tool call budget. When exhausted AI continues without tools — never hard-crashes chat. Enforced per calendar month. 0 = unlimited. Main AI cost driver.",
    enforced: true,
    tiers: { free: "0 (disabled)", starter: "1,000", professional: "25,000", enterprise: "250,000" },
    competitors: [
      { name: "Intercom (Fin AI)", value: "Per resolution pricing" },
      { name: "Zendesk (AI)", value: "Per resolution pricing" },
      { name: "ChatGPT (API)", value: "Per token, no session cap" },
      { name: "Claude API", value: "Per token, no session cap" }
    ]
  },
  // ── Reporting / Data ──────────────────────────────────────────────────────
  {
    label: "Metrics Retention",
    description: "How far back reports can query. Not yet enforced — reports query all data. Store target for performance sprint. Longer retention = higher DB storage cost.",
    enforced: false,
    tiers: { free: "30 days", starter: "90 days", professional: "1 year", enterprise: "3 years" },
    competitors: [
      { name: "Zendesk (Suite Team)", value: "1 year" },
      { name: "Salesforce", value: "18 months" },
      { name: "Google Analytics (free)", value: "14 months" },
      { name: "Intercom (Starter)", value: "90 days" },
      { name: "Mixpanel (free)", value: "90 days" }
    ]
  },
  // ── API / Integrations ────────────────────────────────────────────────────
  {
    label: "Webhook Endpoints",
    description: "Outbound webhooks configured per site. Not yet enforced. Free tier webhook abuse can burn compute on retries.",
    enforced: false,
    tiers: { free: "1", starter: "5", professional: "50", enterprise: "500" },
    competitors: [
      { name: "Zapier (free)", value: "5" },
      { name: "HubSpot (free)", value: "5" },
      { name: "Stripe", value: "Very high" },
      { name: "Twilio", value: "Very high" }
    ]
  },
  // ── File Library / Documents ──────────────────────────────────────────────
  {
    label: "Shared File Library",
    description: "Files uploaded to the document library. Each file can be indexed for bot search and/or made available for agents and bots to send to customers. Counted site-wide regardless of indexing status.",
    enforced: false,
    tiers: { free: "10", starter: "50", professional: "500", enterprise: "5,000" },
    competitors: [
      { name: "Intercom", value: "Not available" },
      { name: "Zendesk (Suite)", value: "Shared article attachments only" },
      { name: "Freshdesk", value: "Solution article attachments" },
      { name: "HubSpot (free)", value: "Documents in CRM, separate from chat" }
    ]
  }
];

export const PACKAGES: PackageDef[] = [
  {
    id: "lead-pro",
    name: "LeadPro",
    tagline: "Turn website visitors into qualified leads — automatically.",
    description:
      "AI-powered lead capture that qualifies prospects in real time. Visitors answer your questions, your CRM gets the data, and your sales team gets a warm, scored handoff — not a cold form submission.",
    icon: "fa-rocket",
    accentColor: "#2563EB",
    features: [
      "Intelligent chat widget with custom qualifying questions",
      "Real-time CRM sync — HubSpot, Dynamics 365, and more",
      "Automatic lead routing to the right sales rep",
      "SMS and email follow-up when agents are unavailable",
      "Lead source tracking (UTM / page URL)",
      "AI bot handles qualification 24/7"
    ],
    requiredFlags: ["enableSms", "enablePageWidgets"],
    wizardSteps: [
      {
        title: "Connect your CRM",
        description: "Push leads directly into HubSpot, Dynamics 365, or any system via webhook. Skip if you want leads in the Velaro inbox only.",
        componentKey: "lead-pro-crm"
      },
      {
        title: "Set your qualifying questions",
        description: "Define the questions your bot asks every visitor — name, email, need, budget, timeline. We give you a head start with proven defaults.",
        componentKey: "lead-pro-questions"
      },
      {
        title: "Deploy the chat widget",
        description: "Copy your embed snippet and drop it before </body>. The widget appears on every page. Use Display Rules to limit it to specific pages.",
        componentKey: "lead-pro-deploy"
      },
      {
        title: "Set up follow-up",
        description: "When no agent is available, send an automatic SMS or email so the lead doesn't go cold. Customize the message and timing.",
        componentKey: "lead-pro-followup"
      },
      {
        title: "You're ready",
        description: "Your lead capture system is live. Watch leads appear in your CRM in real time.",
        componentKey: "lead-pro-done"
      }
    ]
  },
  {
    id: "text-connect",
    name: "TextConnect",
    tagline: "Meet customers where they are — in their text inbox.",
    description:
      "Professional SMS engagement for businesses that know their customers prefer texting. Two-way conversations, automated follow-ups, and campaign broadcasts — all managed from one workspace.",
    icon: "fa-comment-sms",
    accentColor: "#059669",
    features: [
      "Two-way SMS conversations from a dedicated business number",
      "Click-to-Text widget — visitors text you directly from your site",
      "Automated appointment and follow-up reminders via SMS",
      "Campaign broadcasts to your contact list",
      "Auto-reply templates when agents are offline",
      "Full conversation history per contact"
    ],
    requiredFlags: ["enableSms", "enablePageWidgets"],
    wizardSteps: [
      {
        title: "Connect your SMS number",
        description: "Use your existing Twilio number or get a new dedicated business number. We walk you through the setup in minutes.",
        componentKey: "text-connect-sms"
      },
      {
        title: "Set your auto-reply",
        description: "When agents are offline, send an automatic reply so customers know you'll be in touch. Customize the message for business hours and after hours.",
        componentKey: "text-connect-autoreply"
      },
      {
        title: "Add a Click-to-Text widget",
        description: "Put a floating 'Text Us' button on your website. Visitors tap it, enter their number, and you pick up the conversation in your workspace.",
        componentKey: "text-connect-widget"
      },
      {
        title: "Send a test message",
        description: "Send a test SMS to yourself to confirm everything is working end-to-end.",
        componentKey: "text-connect-test"
      },
      {
        title: "You're ready",
        description: "TextConnect is live. Your customers can reach you by text, and you can reach them too.",
        componentKey: "text-connect-done"
      }
    ]
  },
  {
    id: "support-desk",
    name: "SupportDesk",
    tagline: "Every customer channel. One workspace.",
    description:
      "Bring email, chat, and SMS into a single queue. Your team resolves faster because they're not switching tabs or missing messages. Smart routing, canned responses, and a self-serve knowledge base — all included.",
    icon: "fa-headset",
    accentColor: "#7C3AED",
    features: [
      "Unified inbox: email, chat, and SMS in one place",
      "Smart routing — right team, right agent, every time",
      "Canned responses and AI-suggested replies",
      "SLA timers and automatic escalation rules",
      "Self-serve knowledge base to deflect common questions",
      "Full reporting: handle time, CSAT, queue wait"
    ],
    requiredFlags: ["enableSms", "enableKnowledgeBase"],
    wizardSteps: [
      {
        title: "Connect your support email",
        description: "Forward your support@ or help@ inbox to Velaro. Every inbound email becomes a tracked conversation.",
        componentKey: "support-desk-email"
      },
      {
        title: "Set up your support team",
        description: "Create your support team, add agents, and configure routing so every conversation lands with the right person.",
        componentKey: "support-desk-team"
      },
      {
        title: "Deploy the chat widget",
        description: "Add live chat to your website or help center. Visitors get instant answers — or a ticket if agents are offline.",
        componentKey: "support-desk-chat"
      },
      {
        title: "Add your top FAQs",
        description: "Publish your 5 most common questions to the knowledge base. Your bot answers them automatically, freeing your team for complex issues.",
        componentKey: "support-desk-kb"
      },
      {
        title: "You're ready",
        description: "SupportDesk is live. Every customer message — email, chat, or SMS — lands in one place.",
        componentKey: "support-desk-done"
      }
    ]
  },
  {
    id: "appointment-pro",
    name: "AppointmentPro",
    tagline: "Zero no-shows. Automated reminders that actually work.",
    description:
      "Connect your calendar and let Velaro handle the follow-through. Automatic reminders go out before every appointment. Customers confirm or cancel by reply. Your calendar stays clean and your team stays informed.",
    icon: "fa-calendar-check",
    accentColor: "#D97706",
    features: [
      "Outlook and Google Calendar sync",
      "Automated SMS and email reminders — custom timing",
      "Reply CONFIRM / CANCEL — no app required for customers",
      "Automatic rebooking offer on cancellation",
      "Agent notification on every change",
      "Full appointment history per contact"
    ],
    requiredFlags: ["enableSms", "enableAppointmentReminders"],
    wizardSteps: [
      {
        title: "Connect your calendar",
        description: "Authorize Velaro to read your Google Calendar or Outlook/Microsoft 365. We only read — we never modify events unless you enable the confirm/cancel flow.",
        componentKey: "appt-pro-calendar"
      },
      {
        title: "Set reminder timing",
        description: "Choose when reminders go out — 48 hours before, 2 hours before, or both. Add as many reminder touchpoints as you need.",
        componentKey: "appt-pro-timing"
      },
      {
        title: "Write your reminder message",
        description: "Customize the message your customers receive. Use {customerName}, {appointmentTime}, and {agentName} to personalize each reminder automatically.",
        componentKey: "appt-pro-message"
      },
      {
        title: "Enable confirm / cancel",
        description: "Let customers reply YES to confirm or NO to cancel. On cancellation, Velaro notifies your agent and optionally offers a rebooking link.",
        componentKey: "appt-pro-confirmcancel"
      },
      {
        title: "You're ready",
        description: "AppointmentPro is live. Your next appointment reminder will go out automatically.",
        componentKey: "appt-pro-done"
      }
    ]
  },
  {
    id: "commerce-connect",
    name: "CommerceConnect",
    tagline: "Recover carts. Answer product questions. Win repeat buyers.",
    description:
      "E-commerce engagement that runs 24/7. Recover abandoned carts via SMS or chat, answer product questions instantly with a smart page widget, and follow up after every purchase — all without lifting a finger.",
    icon: "fa-bag-shopping",
    accentColor: "#DC2626",
    features: [
      "BigCommerce and Shopify integration",
      "Abandoned cart recovery via SMS or live chat",
      "Product Inquiry widget — context-aware 'Ask about this product'",
      "Post-purchase follow-up and review request automation",
      "AI-powered Q&A from your product catalog",
      "Order status self-service via chat bot"
    ],
    requiredFlags: ["enableSms", "enablePageWidgets"],
    wizardSteps: [
      {
        title: "Connect your store",
        description: "Link your BigCommerce or Shopify store. Velaro will read your product catalog for AI Q&A and watch for abandoned carts.",
        componentKey: "commerce-store"
      },
      {
        title: "Set up cart recovery",
        description: "Choose how long after abandonment to reach out, which channel (SMS or chat), and what to say. A simple 'Still interested?' recovers 5–15% of abandoned carts on average.",
        componentKey: "commerce-cart"
      },
      {
        title: "Deploy the Product Inquiry widget",
        description: "Add the Product Inquiry widget to your product pages. It reads the product name from the page and opens a personalized conversation — no extra config needed per product.",
        componentKey: "commerce-widget"
      },
      {
        title: "Configure post-purchase follow-up",
        description: "After an order is placed, send a follow-up message: shipping confirmation, review request, or upsell. Set the timing and the message.",
        componentKey: "commerce-postpurchase"
      },
      {
        title: "You're ready",
        description: "CommerceConnect is live. Your store is now proactively engaging customers at every stage.",
        componentKey: "commerce-done"
      }
    ]
  }
];
